/**
 * Envoltura de transacciones (Req. 1.4, 18.5).
 *
 * Toda operacion que escriba en mas de una tabla debe pasar por aqui. Si algo
 * falla a mitad del camino, IndexedDB revierte y la base queda en el estado
 * previo. Es lo que evita que una produccion descuente el inventario a medias
 * si la aplicacion se cierra durante el guardado.
 */

import { db } from './schema';
import { ErrorDominio } from '@/domain/errors';
import type { Table } from 'dexie';

/** Todas las tablas. Para operaciones amplias como importar un respaldo. */
export function todasLasTablas(): Table[] {
  return db.tables;
}

/**
 * Ejecuta una operacion de escritura dentro de una transaccion atomica.
 *
 * @example
 *   await enEscritura([db.ingredients, db.inventoryMovements], async () => {
 *     await db.ingredients.update(id, cambios);
 *     await db.inventoryMovements.add(movimiento);
 *   });
 */
export async function enEscritura<T>(
  tablas: readonly Table[],
  operacion: () => Promise<T>,
): Promise<T> {
  try {
    return await db.transaction('rw', tablas as Table[], operacion);
  } catch (error) {
    throw traducirError(error);
  }
}

/** Ejecuta una operacion de lectura consistente dentro de una transaccion. */
export async function enLectura<T>(
  tablas: readonly Table[],
  operacion: () => Promise<T>,
): Promise<T> {
  try {
    return await db.transaction('r', tablas as Table[], operacion);
  } catch (error) {
    throw traducirError(error);
  }
}

/**
 * Traduce errores de IndexedDB a mensajes en espanol.
 *
 * Los errores del dominio pasan sin tocarse: ya traen un mensaje pensado para
 * mostrarse en pantalla.
 */
export function traducirError(error: unknown): Error {
  if (error instanceof ErrorDominio) return error;

  if (error instanceof Error) {
    const nombre = error.name;

    if (nombre === 'QuotaExceededError') {
      return new ErrorDominio(
        'cuota_agotada',
        'El almacenamiento del dispositivo esta lleno y no se pudo guardar. ' +
          'Haz un respaldo y libera espacio en el telefono antes de continuar.',
      );
    }

    if (nombre === 'ConstraintError') {
      return new ErrorDominio(
        'restriccion',
        'Ya existe un registro con ese identificador. No se guardo nada.',
      );
    }

    if (nombre === 'VersionError' || nombre === 'UpgradeError') {
      return new ErrorDominio(
        'version_base',
        'La base de datos guardada en este dispositivo es de una version mas ' +
          'reciente que la aplicacion. Actualiza la aplicacion antes de continuar.',
      );
    }

    if (nombre === 'DatabaseClosedError') {
      return new ErrorDominio(
        'base_cerrada',
        'Se perdio la conexion con la base de datos. Cierra y vuelve a abrir la aplicacion.',
      );
    }

    if (nombre === 'AbortError') {
      return new ErrorDominio(
        'transaccion_abortada',
        'La operacion se interrumpio y no se guardo nada. Vuelve a intentarlo.',
      );
    }

    return new ErrorDominio('desconocido', `No se pudo completar la operacion: ${error.message}`);
  }

  return new ErrorDominio('desconocido', 'No se pudo completar la operacion.');
}

/** Extrae un mensaje presentable de cualquier error. */
export function mensajeDeError(error: unknown): string {
  if (error instanceof ErrorDominio) return error.message;
  if (error instanceof Error) return traducirError(error).message;
  return 'Ocurrio un error inesperado.';
}
